import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Wallet, Users2, Truck, FileText, Loader2, ArrowDownLeft, ArrowUpRight, Coins } from "lucide-react";
import { finApi, money, apiErr } from "../api";
import { SaudiRiyal } from "../../components/SaudiRiyal";
import { toast } from "sonner";
import { PageHeader, Card } from "../FinanceApp";

const Kpi = ({ icon: Icon, label, value, tone = "bg-slate-100 text-slate-600", suffix = <SaudiRiyal /> }) => (
  <Card className="p-4" >
    <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${tone}`}><Icon className="h-4.5 w-4.5" /></div>
    <div className="mt-3 text-xl font-extrabold text-slate-900 font-mono-nums" dir="ltr">{money(value)} <SaudiRiyal /> <span className="text-xs text-slate-400">{suffix}</span></div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </Card>
);

export default function FinDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { finApi.dashboard().then(setD).catch((e) => toast.error(apiErr(e))); }, []);
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-500" /></div>;
  return (
    <div data-testid="fin-dashboard">
      <PageHeader title="لوحة التحكم المالية" subtitle="نظرة عامة على الوضع المالي للشركة" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={TrendingUp} label="الإيرادات" value={d.revenue} tone="bg-emerald-50 text-emerald-600" />
        <Kpi icon={TrendingDown} label="المصروفات" value={d.expenses} tone="bg-rose-50 text-rose-600" />
        <Kpi icon={Coins} label="صافي الربح" value={d.net_profit} tone="bg-blue-50 text-blue-600" />
        <Kpi icon={Wallet} label="النقد والبنوك" value={d.cash} tone="bg-amber-50 text-amber-600" />
        <Kpi icon={ArrowDownLeft} label="ذمم مدينة (عملاء)" value={d.receivables} tone="bg-teal-50 text-teal-600" />
        <Kpi icon={ArrowUpRight} label="ذمم دائنة (موردون)" value={d.payables} tone="bg-orange-50 text-orange-600" />
        <Kpi icon={FileText} label="ضريبة القيمة المضافة المستحقة" value={d.vat_payable} tone="bg-violet-50 text-violet-600" />
        <Kpi icon={Coins} label="حقوق الملكية" value={d.equity} tone="bg-slate-100 text-slate-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
        <Card className="p-4">
          <div className="text-sm font-bold text-slate-800 mb-3">إحصائيات سريعة</div>
          <ul className="space-y-2 text-sm">
            <Stat label="عدد الحسابات" value={d.counts.accounts} />
            <Stat label="العملاء" value={d.counts.customers} />
            <Stat label="الموردون" value={d.counts.suppliers} />
            <Stat label="قيود مُرحّلة" value={d.counts.journals_posted} />
            <Stat label="قيود مسودة" value={d.counts.journals_draft} />
          </ul>
        </Card>
        <Card className="p-4 lg:col-span-2">
          <div className="text-sm font-bold text-slate-800 mb-3">أحدث القيود</div>
          {d.recent.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">لا توجد قيود بعد</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-right">
                <thead className="text-xs text-slate-500 border-b border-slate-100">
                  <tr><th className="py-2 font-semibold">رقم</th><th className="py-2 font-semibold">التاريخ</th><th className="py-2 font-semibold">البيان</th><th className="py-2 font-semibold">المبلغ</th><th className="py-2 font-semibold">الحالة</th></tr>
                </thead>
                <tbody className="font-mono-nums">
                  {d.recent.map((e) => (
                    <tr key={e.id} className="border-b border-slate-50">
                      <td className="py-2">{e.number}</td>
                      <td className="py-2">{e.date}</td>
                      <td className="py-2 font-sans">{e.description || "—"}</td>
                      <td className="py-2">{money(e.total_debit)} <SaudiRiyal /></td>
                      <td className="py-2 font-sans"><StatusBadge s={e.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <li className="flex items-center justify-between"><span className="text-slate-500">{label}</span><span className="font-bold text-slate-800 font-mono-nums">{value}</span></li>
);

export const StatusBadge = ({ s }) => {
  const m = {
    draft: ["مسودة", "bg-slate-100 text-slate-600"],
    posted: ["مُرحّل", "bg-emerald-50 text-emerald-700"],
    reversed: ["معكوس", "bg-amber-50 text-amber-700"],
    cancelled: ["ملغى", "bg-rose-50 text-rose-700"],
  }[s] || [s, "bg-slate-100 text-slate-600"];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${m[1]}`}>{m[0]}</span>;
};
